import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useUser } from "@clerk/clerk-react";
import {
  Network,
  Building2,
  FolderOpen,
  Monitor,
  Calendar,
  Globe,
  Server,
  Hash,
} from "lucide-react";
import { fetchOnboardingTree } from "../../../features/configuration/configurationSlice";
import TreeDataTable from "../../../components/Common/TreeDataTable";
import { useClientFilter } from "../../../hooks/useClientFilter";
import "./system-integrations.scss";

export default function SystemIntegrations() {
  const dispatch = useDispatch();
  const { user } = useUser();
  const { onboardingTree, onboardingTreeStatus, onboardingTreeError } =
    useSelector((state) => state.configuration);

  // Use client filter hook for filtering by selected client
  const { selectedClientId } = useClientFilter();

  // Get client info from Clerk metadata
  const userClient = user?.publicMetadata?.client;
  const isHelxon = userClient?.name === "Helxon";

  useEffect(() => {
    if (onboardingTreeStatus === "idle") {
      dispatch(fetchOnboardingTree());
    }
  }, [dispatch, onboardingTreeStatus]);

  // Filter onboarding tree by selected client
  const filteredOnboardingTree = useMemo(() => {
    if (!onboardingTree || onboardingTree.length === 0) return [];
    if (!selectedClientId) return onboardingTree; // Return all if no client selected
    
    // Filter to show only the selected client
    return onboardingTree.filter(
      (client) => client.clientUUID === selectedClientId
    );
  }, [onboardingTree, selectedClientId]);

  // For non-Helxon users, get their specific client data
  const clientData = useMemo(() => {
    if (isHelxon || !filteredOnboardingTree || filteredOnboardingTree.length === 0) return null;
    // For non-Helxon, the tree should contain only their client's data
    return filteredOnboardingTree[0];
  }, [isHelxon, filteredOnboardingTree]);

  // Define columns for the tree table (Helxon view)
  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        width: "25%",
        accessorFn: (row) => {
          switch (row.type) {
            case "client":
              return row.data.clientName;
            case "workspace":
              return row.data.workspaceName;
            case "asset":
              return row.data.deviceName;
            default:
              return "-";
          }
        },
      },
      {
        id: "uuid",
        header: "UUID",
        width: "20%",
        accessorFn: (row) => {
          switch (row.type) {
            case "client":
              return row.data.clientUUID;
            case "workspace":
              return row.data.workspaceUUID;
            case "asset":
              return row.data.assetUUID;
            default:
              return "-";
          }
        },
        cell: ({ value }) => (
          <span className="tdt-uuid" title={value}>
            {value ? `${value}` : "-"}
          </span>
        ),
      },
      {
        id: "type",
        header: "Type",
        width: "10%",
        accessorFn: (row) => {
          switch (row.type) {
            case "client":
              return "Client";
            case "workspace":
              return "Workspace";
            case "asset":
              return row.data.deviceType || "Asset";
            default:
              return "-";
          }
        },
        cell: ({ row, value }) => (
          <span className={`tdt-type-badge tdt-type-badge--${row.type}`}>
            {value}
          </span>
        ),
      },
      {
        id: "description",
        header: "Description",
        width: "20%",
        accessorFn: (row) => {
          switch (row.type) {
            case "client":
            case "workspace":
              return row.data.description;
            case "asset":
              return "-";
            default:
              return "-";
          }
        },
      },
      {
        id: "status",
        header: "Status",
        width: "10%",
        accessorFn: (row) => {
          if (row.type === "client") {
            return row.data.status ? "Active" : "Inactive";
          }
          return "-";
        },
        cell: ({ row, value }) => {
          if (row.type !== "client") return "-";
          const isActive = row.data.status;
          return (
            <span
              className={`tdt-status tdt-status--${
                isActive ? "active" : "inactive"
              }`}
            >
              <span className="tdt-status__dot" />
              {value}
            </span>
          );
        },
      },
      {
        id: "createdAt",
        header: "Created",
        width: "15%",
        accessorFn: (row) => {
          const date = row.data.createdAt || row.data.createdTime;
          if (!date) return "-";
          return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        },
      },
      {
        id: "modifiedAt",
        header: "Modified",
        width: "15%",
        accessorFn: (row) => {
          const date = row.data.modifiedAt || row.data.modifiedTime;
          if (!date) return "-";
          return new Date(date).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          });
        },
      },
      {
        id: "workspaceID",
        header: "Workspace ID",
        width: "20%",
        accessorFn: (row) => {
          if (row.type === "workspace") {
            return row.data.workspaceID;
          }
          return "-";
        },
        cell: ({ value }) => (
          <span className="tdt-uuid" title={value}>
            {value && value !== "-" ? `${value.substring(0, 8)}...` : "-"}
          </span>
        ),
      },
      {
        id: "clientUUID",
        header: "Client UUID",
        width: "20%",
        accessorFn: (row) => {
          if (row.type === "workspace" || row.type === "asset") {
            return row.data.clientUUID;
          }
          return "-";
        },
        cell: ({ value }) => (
          <span className="tdt-uuid" title={value}>
            {value && value !== "-" ? `${value.substring(0, 8)}...` : "-"}
          </span>
        ),
      },
      {
        id: "ip",
        header: "IP Address",
        width: "12%",
        accessorFn: (row) => {
          if (row.type === "asset") {
            return row.data.ip || "-";
          }
          return "-";
        },
      },
      {
        id: "hostname",
        header: "Hostname",
        width: "15%",
        accessorFn: (row) => {
          if (row.type === "asset") {
            return row.data.hostname || "-";
          }
          return "-";
        },
      },
      {
        id: "deviceType",
        header: "Device Type",
        width: "12%",
        accessorFn: (row) => {
          if (row.type === "asset") {
            return row.data.deviceType || "-";
          }
          return "-";
        },
      },
      {
        id: "logTypeMethod",
        header: "Log Type Method",
        width: "15%",
        accessorFn: (row) => {
          if (row.type === "asset") {
            return row.data.logTypeMethod || "-";
          }
          return "-";
        },
      },
    ],
    []
  );

  const initialColumnVisibility = useMemo(
    () => ({
      name: true,
      uuid: true,
      type: true,
      description: true,
      status: true,
      createdAt: true,
      modifiedAt: false,
      workspaceID: false,
      clientUUID: false,
      ip: false,
      hostname: false,
      deviceType: false,
      logTypeMethod: false,
    }),
    []
  );

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Card View for non-Helxon users
  const renderCardView = () => {
    if (!clientData) {
      return (
        <div className="si-empty">
          <p>No data available for your organization.</p>
        </div>
      );
    }

    return (
      <div className="si-card-view">
        {/* Client Info Card */}
        <div className="si-client-card">
          <div className="si-client-card__header">
            <Building2 size={24} className="si-client-card__icon" />
            <div className="si-client-card__title-section">
              <h3 className="si-client-card__title">{clientData.clientName}</h3>
              <span
                className={`si-status si-status--${
                  clientData.status ? "active" : "inactive"
                }`}
              >
                <span className="si-status__dot" />
                {clientData.status ? "Active" : "Inactive"}
              </span>
            </div>
          </div>
          <p className="si-client-card__description">
            {clientData.description}
          </p>
          <div className="si-client-card__meta">
            <div className="si-meta-item">
              <Hash size={14} />
              <span className="si-meta-item__label">UUID:</span>
              <span
                className="si-meta-item__value si-uuid"
                title={clientData.clientUUID}
              >
                {clientData.clientUUID}
              </span>
            </div>
            <div className="si-meta-item">
              <Calendar size={14} />
              <span className="si-meta-item__label">Created:</span>
              <span className="si-meta-item__value">
                {formatDate(clientData.createdAt)}
              </span>
            </div>
            <div className="si-meta-item">
              <Calendar size={14} />
              <span className="si-meta-item__label">Modified:</span>
              <span className="si-meta-item__value">
                {formatDate(clientData.modifiedAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Workspaces Section */}
        <div className="si-section">
          <div className="si-section__header">
            <FolderOpen size={20} />
            <h4 className="si-section__title">Workspaces</h4>
            <span className="si-section__count">
              {clientData.workspaces?.length || 0}
            </span>
          </div>

          {!clientData.workspaces || clientData.workspaces.length === 0 ? (
            <div className="si-empty-section">
              <p>No workspaces configured.</p>
            </div>
          ) : (
            <div className="si-cards-grid">
              {clientData.workspaces.map((workspace) => (
                <div
                  key={workspace.workspaceUUID}
                  className="si-workspace-card"
                >
                  <div className="si-workspace-card__header">
                    <FolderOpen size={18} className="si-workspace-card__icon" />
                    <h5 className="si-workspace-card__title">
                      {workspace.workspaceName}
                    </h5>
                  </div>
                  <p className="si-workspace-card__description">
                    {workspace.description}
                  </p>
                  <div className="si-workspace-card__meta">
                    <div className="si-meta-item">
                      <Hash size={12} />
                      <span
                        className="si-meta-item__value si-uuid"
                        title={workspace.workspaceUUID}
                      >
                        {workspace.workspaceUUID.substring(0, 8)}...
                      </span>
                    </div>
                    <div className="si-meta-item">
                      <Calendar size={12} />
                      <span className="si-meta-item__value">
                        {formatDate(workspace.createdAt)}
                      </span>
                    </div>
                  </div>

                  {/* Assets within workspace */}
                  {workspace.assets && workspace.assets.length > 0 && (
                    <div className="si-assets-section">
                      <div className="si-assets-section__header">
                        <Monitor size={14} />
                        <span>Assets ({workspace.assets.length})</span>
                      </div>
                      <div className="si-assets-list">
                        {workspace.assets.map((asset) => (
                          <div key={asset.assetUUID} className="si-asset-item">
                            <div className="si-asset-item__header">
                              <Monitor
                                size={14}
                                className="si-asset-item__icon"
                              />
                              <span className="si-asset-item__name">
                                {asset.deviceName}
                              </span>
                              {asset.deviceType && (
                                <span className="si-asset-item__type">
                                  {asset.deviceType}
                                </span>
                              )}
                            </div>
                            <div className="si-asset-item__details">
                              {asset.hostname && (
                                <div className="si-asset-detail">
                                  <Server size={12} />
                                  <span>{asset.hostname}</span>
                                </div>
                              )}
                              {asset.ip && (
                                <div className="si-asset-detail">
                                  <Globe size={12} />
                                  <span>{asset.ip}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Loading state
  if (onboardingTreeStatus === "loading") {
    return (
      <div className="system-integrations">
        <div className="system-integrations__header">
          <div className="system-integrations__title-section">
            <Network size={20} />
            <h3 className="system-integrations__title">Workspaces & Assets</h3>
          </div>
        </div>
        <div className="si-loading">
          <div className="si-loading__spinner" />
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="system-integrations">
      <div className="system-integrations__header">
        <div className="system-integrations__title-section">
          <Network size={20} />
          <h3 className="system-integrations__title">Workspaces & Assets</h3>
        </div>
        {isHelxon && <button className="btn btn--primary">Add Client</button>}
      </div>

      {onboardingTreeStatus === "failed" && (
        <div className="system-integrations__error">
          Failed to load data: {onboardingTreeError}
        </div>
      )}

      {isHelxon ? (
        // Helxon admin view - Tree Table (filtered by selected client)
        <TreeDataTable
          data={filteredOnboardingTree}
          columns={columns}
          loading={onboardingTreeStatus === "loading"}
          emptyMessage="No clients, workspaces, or assets found"
          defaultExpandedLevel={1}
          enableSearch={true}
          enableColumnVisibility={true}
          enableDensity={true}
          enableExport={true}
          enablePagination={true}
          pageSize={20}
          initialColumnVisibility={initialColumnVisibility}
        />
      ) : (
        // Non-Helxon client view - Card View
        renderCardView()
      )}
    </div>
  );
}
